import React, { Suspense, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Eagerly load homepage (critical path)
import HomePage from './pages/HomePage';

// Lazy load everything else
const GaragesPage = React.lazy(() => import('./pages/GaragesPage'));
const GarageDetailPage = React.lazy(() => import('./pages/GarageDetailPage'));
const VenuePage = React.lazy(() => import('./pages/VenuePage'));
const VenuesPage = React.lazy(() => import('./pages/VenuesPage'));
const EventPage = React.lazy(() => import('./pages/EventPage'));
const TeamPage = React.lazy(() => import('./pages/TeamPage'));
const EventsPage = React.lazy(() => import('./pages/EventsPage'));
const CitiesPage = React.lazy(() => import('./pages/CitiesPage'));
const CityPage = React.lazy(() => import('./pages/CityPage'));
const MethodologyPage = React.lazy(() => import('./pages/MethodologyPage'));
const TeamsPage = React.lazy(() => import('./pages/TeamsPage'));
const BlogPage = React.lazy(() => import('./pages/BlogPage'));
const BlogStatePage = React.lazy(() => import('./pages/BlogStatePage'));
const BlogCityPage = React.lazy(() => import('./pages/BlogCityPage'));
const BlogTeamPage = React.lazy(() => import('./pages/BlogTeamPage'));
const BlogLeaguePage = React.lazy(() => import('./pages/BlogLeaguePage'));
const BlogWorstExits = React.lazy(() => import('./pages/BlogWorstExits'));
const BlogCityRankings = React.lazy(() => import('./pages/BlogCityRankings'));
const BlogDirectionPage = React.lazy(() => import('./pages/BlogDirectionPage'));
const BlogBudgetPage = React.lazy(() => import('./pages/BlogBudgetPage'));
const BlogMatchupPage = React.lazy(() => import('./pages/BlogMatchupPage'));
const BlogEventPage = React.lazy(() => import('./pages/BlogEventPage'));
const BlogAvoidPage = React.lazy(() => import('./pages/BlogAvoidPage'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div
        className="w-6 h-6 rounded-full animate-spin"
        style={{
          border: '2px solid var(--border)',
          borderTopColor: '#10b981',
        }}
      />
    </div>
  );
}

function App() {
  const { pathname } = useLocation();
  // The homepage and the relit garage detail page ship their own light
  // nav/footer chrome; suppress the global dark Header/Footer on those routes.
  // All other routes keep them unchanged.
  const isHome = pathname === '/';
  const isGarageDetail = /^\/garages\/[^/]+$/.test(pathname);
  const suppressChrome = isHome || isGarageDetail;
  return (
    <div className="min-h-screen flex flex-col relative">
      <ScrollToTop />
      {!suppressChrome && <Header />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/austin" element={<Navigate to="/" replace />} />
            <Route path="/cities" element={<CitiesPage />} />
            <Route path="/cities/:citySlug" element={<CityPage />} />
            <Route path="/methodology" element={<MethodologyPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/worst-exits" element={<BlogWorstExits />} />
            <Route path="/blog/city-rankings" element={<BlogCityRankings />} />
            <Route path="/blog/league/:league" element={<BlogLeaguePage />} />
            <Route path="/blog/:stateCode" element={<BlogStatePage />} />
            <Route path="/blog/:stateCode/:citySlug" element={<BlogCityPage />} />
            <Route path="/blog/matchups/:matchup" element={<BlogMatchupPage />} />
            <Route path="/blog/events/:eventSlug" element={<BlogEventPage />} />
            <Route path="/blog/:stateCode/:citySlug/:teamSlug/heading-:direction" element={<BlogDirectionPage />} />
            <Route path="/blog/:stateCode/:citySlug/:teamSlug/avoid-heading-:direction" element={<BlogAvoidPage />} />
            <Route path="/blog/:stateCode/:citySlug/:teamSlug/cheap-parking" element={<BlogBudgetPage />} />
            <Route path="/blog/:stateCode/:citySlug/:teamSlug" element={<BlogTeamPage />} />
            <Route path="/garages" element={<GaragesPage />} />
            <Route path="/garages/:slug" element={<GarageDetailPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:slug" element={<EventPage />} />
            <Route path="/venues" element={<VenuesPage />} />
            <Route path="/venues/:slug" element={<VenuePage />} />
            <Route path="/teams" element={<TeamsPage />} />
            <Route path="/teams/:slug" element={<TeamPage />} />
            <Route path="*" element={
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                  <h1 className="text-6xl font-black mb-4" style={{ color: 'var(--text-faint)' }}>404</h1>
                  <p style={{ color: 'var(--text-muted)' }}>Page not found</p>
                </div>
              </div>
            } />
          </Routes>
        </Suspense>
      </main>
      {!suppressChrome && <Footer />}
      <Analytics />
    </div>
  );
}

export default App;
